package macha.sh.kotlin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import macha.sh.kotlin.SiteColors
import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.applyFont
import macha.sh.kotlin.components.layouts.PageLayoutData
import macha.sh.kotlin.components.layouts.SiteContentMaxWidth
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlinx.browser.window
import kotlinx.coroutines.await

data class RepoItem(
    val name: String,
    val description: String,
    val source: String,
    val language: String?,
    val stars: Int,
    val forks: Int,
    val updatedAt: String?,
    val url: String,
    val cloneUrl: String?
)

object ReposLoader {
    suspend fun load(url: String = "/api/repos"): List<RepoItem> {
        return try {
            val resp = window.fetch(url).await()
            val targetResp = if (resp.ok) resp else window.fetch("/text-content/repos.json").await()
            if (!targetResp.ok) return emptyList()
            val txt = targetResp.text().await()
            val parsed = js("JSON.parse")(txt) as Array<dynamic>
            parsed.map { r ->
                RepoItem(
                    name = (r.name as? String) ?: "",
                    description = (r.description as? String) ?: "",
                    source = (r.source as? String) ?: "github",
                    language = r.language as? String,
                    stars = (r.stars as? Number)?.toInt() ?: 0,
                    forks = (r.forks as? Number)?.toInt() ?: 0,
                    updatedAt = r.updatedAt as? String,
                    url = (r.url as? String) ?: "",
                    cloneUrl = r.cloneUrl as? String
                )
            }
        } catch (e: Throwable) {
            console.error("Failed to load repository list: ", e)
            emptyList()
        }
    }
}

@InitRoute
fun initReposPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Repositories",
            description = "Public software projects, self-hosted Forgejo repositories, and open-source packages by Macha.",
            path = "/repos",
            indexable = false,
        )
    )
}

enum class RepoFilter { All, Forgejo, GitHub }

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun ReposPage() {
    var repos by remember { mutableStateOf<List<RepoItem>>(emptyList()) }
    var filter by remember { mutableStateOf(RepoFilter.All) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        repos = ReposLoader.load()
        isLoading = false
    }

    val filteredRepos = when (filter) {
        RepoFilter.All -> repos
        RepoFilter.Forgejo -> repos.filter { it.source.lowercase() == "forgejo" }
        RepoFilter.GitHub -> repos.filter { it.source.lowercase() == "github" }
    }

    val forgejoCount = repos.count { it.source.lowercase() == "forgejo" }
    val githubCount = repos.count { it.source.lowercase() == "github" }

    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(SiteContentMaxWidth)
                .gap(1.5.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header & Description
            Box(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(SiteColors.BoxBkg)
                    .border(width = 2.px, style = LineStyle.Solid, color = SiteColors.Border)
                    .padding(1.5.cssRem)
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().gap(0.75.cssRem).textAlign(TextAlign.Center),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    H1(
                        attrs = Modifier
                            .applyFont(SiteTypography.Subheading, fontSize = 2.cssRem)
                            .color(SiteColors.Hover)
                            .toAttrs()
                    ) {
                        Text("REPOSITORIES & CODE MIRRORS")
                    }
                    SpanText(
                        "Public software projects, tools, and libraries developed by Macha across self-hosted Forgejo infrastructure and GitHub.",
                        modifier = Modifier.applyFont(SiteTypography.Body, fontSize = 1.15.cssRem)
                    )
                }
            }

            // GitHub Profile Hero Card
            Box(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(SiteColors.BoxBkg)
                    .border(width = 2.px, style = LineStyle.Solid, color = SiteColors.Border)
                    .padding(1.25.cssRem)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().gap(1.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f).gap(0.25.cssRem)) {
                        SpanText(
                            "MAIN GITHUB PROFILE",
                            modifier = Modifier.applyFont(SiteTypography.Subheading).color(SiteColors.Hover)
                        )
                        SpanText(
                            "Looking for the primary hub for open-source contributions, forks, and stars? Explore the full profile and activity stream on GitHub.",
                            modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.SiteText)
                        )
                    }
                    Link(
                        path = "https://github.com/MachaLvl99",
                        text = "Visit @MachaLvl99 →",
                        modifier = Modifier
                            .applyFont(SiteTypography.Subheading)
                            .backgroundColor(SiteColors.SiteBkg)
                            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Hover)
                            .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                            .color(SiteColors.Hover),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
                    )
                }
            }

            // Filter Tabs
            Row(modifier = Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                FilterButton(
                    label = "All (${repos.size})",
                    isSelected = filter == RepoFilter.All,
                    onClick = { filter = RepoFilter.All }
                )
                FilterButton(
                    label = "Forgejo / Self-Hosted ($forgejoCount)",
                    isSelected = filter == RepoFilter.Forgejo,
                    onClick = { filter = RepoFilter.Forgejo }
                )
                FilterButton(
                    label = "GitHub ($githubCount)",
                    isSelected = filter == RepoFilter.GitHub,
                    onClick = { filter = RepoFilter.GitHub }
                )
            }

            // Repos Grid
            if (isLoading) {
                Box(Modifier.fillMaxWidth().padding(2.cssRem), contentAlignment = Alignment.Center) {
                    SpanText("Loading repositories...", modifier = Modifier.applyFont(SiteTypography.Body))
                }
            } else if (filteredRepos.isEmpty()) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(SiteColors.BoxBkg)
                        .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
                        .padding(2.cssRem),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText("No repositories found in this category.", modifier = Modifier.applyFont(SiteTypography.Body))
                }
            } else {
                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fill, minmax(320px, 1fr))")
                        property("gap", "1rem")
                        property("width", "100%")
                    }
                }) {
                    filteredRepos.forEach { repo ->
                        RepoCard(repo)
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterButton(label: String, isSelected: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .backgroundColor(if (isSelected) SiteColors.Hover else SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(topBottom = 0.4.cssRem, leftRight = 0.8.cssRem)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            modifier = Modifier
                .applyFont(SiteTypography.BodyS)
                .color(if (isSelected) SiteColors.SiteBkg else SiteColors.SiteText)
        )
    }
}

@Composable
private fun RepoCard(repo: RepoItem) {
    val isForgejo = repo.source.lowercase() == "forgejo"

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(1.25.cssRem)
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(0.75.cssRem)) {
            // Header Row: Name & Source Chip
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                H2(
                    attrs = Modifier
                        .applyFont(SiteTypography.Subheading, fontSize = 1.2.cssRem)
                        .color(SiteColors.Hover)
                        .weight(1f)
                        .toAttrs()
                ) {
                    Text(repo.name)
                }
                Box(
                    modifier = Modifier
                        .backgroundColor(if (isForgejo) SiteColors.SiteBkg else SiteColors.SiteBkg)
                        .border(
                            width = 1.px,
                            style = LineStyle.Solid,
                            color = if (isForgejo) SiteColors.Hover else SiteColors.Border
                        )
                        .padding(topBottom = 0.2.cssRem, leftRight = 0.5.cssRem)
                ) {
                    SpanText(
                        if (isForgejo) "Forgejo [Self-Hosted]" else "GitHub",
                        modifier = Modifier
                            .applyFont(SiteTypography.BodyS)
                            .color(if (isForgejo) SiteColors.Hover else SiteColors.SiteText)
                    )
                }
            }

            // Description
            SpanText(
                if (repo.description.isNotBlank()) repo.description else "No description provided.",
                modifier = Modifier
                    .fillMaxWidth()
                    .applyFont(SiteTypography.BodyS)
                    .color(SiteColors.SiteText)
            )

            // Meta Info: Language, Stars, Forks, Updated
            Row(
                modifier = Modifier.fillMaxWidth().gap(0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (!repo.language.isNullOrBlank()) {
                    Box(
                        modifier = Modifier
                            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
                            .padding(topBottom = 0.15.cssRem, leftRight = 0.4.cssRem)
                    ) {
                        SpanText(
                            repo.language,
                            modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.Hover)
                        )
                    }
                }
                if (repo.stars > 0) {
                    SpanText("★ ${repo.stars}", modifier = Modifier.applyFont(SiteTypography.BodyS))
                }
                if (repo.forks > 0) {
                    SpanText("⑂ ${repo.forks}", modifier = Modifier.applyFont(SiteTypography.BodyS))
                }
                if (!repo.updatedAt.isNullOrBlank()) {
                    SpanText(
                        "Updated ${repo.updatedAt}",
                        modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.SiteText)
                    )
                }
            }

            // Divider
            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(SiteColors.Border))

            // Action / Link Row
            if (isForgejo) {
                Link(
                    path = repo.url,
                    text = "View on Forgejo →",
                    modifier = Modifier
                        .applyFont(SiteTypography.Subheading)
                        .color(SiteColors.Hover),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
                )
            } else {
                Link(
                    path = repo.url,
                    text = "View Repository on GitHub →",
                    modifier = Modifier
                        .applyFont(SiteTypography.Subheading)
                        .color(SiteColors.Hover),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
                )
            }
        }
    }
}
